import React from 'react';
import { observer } from 'mobx-react';
import { Spring, config, animated } from 'react-spring';
import { AvGroup, AvInput, AvFeedback } from 'availity-mobx-reactstrap-validation';
import { Label as FormLabel } from 'reactstrap';

import PageComponent from 'js/components/pages/PageComponent';
import AppContent from 'js/components/AppContent';
import Modal from 'js/components/core/Modal';
import { lightGrey1Color, startAnimation, endAnimation } from 'js/config/style';
import { url } from 'js/services/Network';

import ReturnOrderPageState from 'js/stores/pages/ReturnOrderPageState';
import Constraint from 'js/components/Constraint';
import Backdrop from 'js/components/core/Backdrop';
import ErrorDisplay from 'js/components/common/ErrorDisplay';
import Button from 'js/components/common/Button';

import PageTitle from './PageTitle';
import ShipIcon from './ShipIcon';
import Container from './Container';
import Grid from './Grid';
import Label from './Label';
import Value from './Value';
import ReturnNotice from './ReturnNotice';
import DropoffNotice from './DropoffNotice';
import ReturnButton from './ReturnButton';
import AvForm from './AvForm';
import ModalTitle from './ModalTitle';
import Textarea from './Textarea';
import OrderNumber from './OrderNumber';
import ItemTitle from './ItemTitle';

const offset = '7.25rem';

@observer
export default class ReturnOrderPage extends PageComponent {
  loginRequired = true;
  pageName = 'ReturnOrderPage';
  pageColor = lightGrey1Color;
  page = ReturnOrderPageState.create({
    uid: this.props.uid,
  });

  backNav = () => ({
    mode: 'back',
    text: this.page.item_title,
    url: url('status-page', { alias: this.page.item_alias }),
  });

  bootstrap = () => this.page.load();

  renderPage() {
    const page = this.page;
    return (
      <>
        <AppContent>
          <Backdrop offset={offset}>
            <Constraint>
              <PageTitle>Return order</PageTitle>
            </Constraint>
          </Backdrop>

          <Constraint offset={offset}>
            <Spring native from={startAnimation} to={endAnimation} config={config.gentle}>
              {animation => (
                <animated.div style={animation}>
                  <Container>
                    <ShipIcon />
                    <Grid>
                      <Label area="order-label" hidesmall>
                        Order
                      </Label>
                      <Value area="order-value">
                        <ItemTitle>{page.item_title}</ItemTitle>
                        <OrderNumber>#{page.uid}</OrderNumber>
                      </Value>
                      <Label area="seller-label">Return to</Label>
                      <Value area="seller-value">@{page.seller}</Value>
                      <Label area="shipping-label">Shipping</Label>
                      <Value area="shipping-value">
                        <div>{page.shipper_label}</div>
                        <div>{page.pickup.address1}</div>
                        {page.pickup.address2 && <div>{page.pickup.address2}</div>}
                        <div>
                          {page.pickup.city} {page.pickup.state}, {page.pickup.zip}
                        </div>
                      </Value>
                    </Grid>
                    <ReturnNotice>
                      Please make sure that you are shipping the item as it was received. If you return the
                      wrong/different item, you will still be charged.
                    </ReturnNotice>
                    {page.requires_drop_off && (
                      <DropoffNotice>
                        You will need to take this item to your nearest {page.shipper_label} with the label we will
                        provide you.
                      </DropoffNotice>
                    )}
                    <ReturnButton onClick={page.showModal}>Return order</ReturnButton>
                  </Container>
                </animated.div>
              )}
            </Spring>
          </Constraint>
        </AppContent>

        <Modal showModal={page.modalIsOpen} onCancel={page.closeModal} withCloseButton>
          <AvForm onValidSubmit={page.returnOrder}>
            <ModalTitle>Return order</ModalTitle>

            <AvGroup>
              <FormLabel>Why are you returning this?</FormLabel>
              <AvInput
                type="select"
                name="reason"
                onChange={page.setReason}
                validate={{
                  validate: reason => reason && reason !== 'default',
                }}>
                <option value="default">Please choose an option</option>
                <option value="not-ordered">It isn't what I ordered</option>
                <option value="bad-condition">It isn't in the same condition as it shows on the ad</option>
                <option value="other">Other</option>
              </AvInput>
              <AvFeedback>Please provide a reason for your return.</AvFeedback>
            </AvGroup>

            <AvGroup>
              <FormLabel for="notes">Notes/Reason</FormLabel>
              <Textarea name="notes" id="notes" required={page.reason === 'other'} />
              <AvFeedback>Please provide a reason for your return.</AvFeedback>
            </AvGroup>

            <Button type="submit" isLoading={page.isReturning}>
              Return order now
            </Button>

            <ErrorDisplay>{page.error}</ErrorDisplay>
          </AvForm>
        </Modal>
      </>
    );
  }
}
